import { useEffect, useState, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import { useSelector, useDispatch } from "react-redux";
import { getHsCode } from "../../Actions/HSCodeAction";
import { globalStyle } from "../../global/Utils";
import { Box } from "@mui/material";
import { isUndefined, debounce } from "lodash";
import { useTheme, useMediaQuery } from "@mui/material";
import MobileViewTabelList from "../Common/MobileViewTabelList";

const HsCodeList = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });
  const [globalFilter, setGlobalFilter] = useState("");

  const isGettingHsCodeList = useSelector(
    (state) => state.HsCodeListReducer?.isGettingHsCodeList,
  );
  const hsCodeList = useSelector(
    (state) => state.HsCodeListReducer?.hsCodeList || [],
  );
  const totalResults = useSelector(
    (state) => state.HsCodeListReducer.totalResults || 0,
  );

  useEffect(() => {
    dispatch(
      getHsCode(pagination.pageIndex, pagination.pageSize, globalFilter),
    );
  }, [pagination, dispatch, globalFilter]);

  const debouncedSetFilter = useMemo(
    () =>
      debounce((value) => {
        setGlobalFilter(value);
      }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSetFilter.cancel();
    };
  }, [debouncedSetFilter]);

  // Define table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: "_id",
        header: "#",
        size: 60,
        enableResizing: false,
        enableEditing: false,
        Cell: ({ row }) =>
          row.index + 1 + pagination.pageIndex * pagination.pageSize, // serial number considering pagination
      },
      {
        accessorKey: "pctCode",
        header: "HS Code",
        size: 180,
        grow: false, // fixed width
        enableEditing: true,
        muiEditTextFieldProps: {
          required: true,
          placeholder: "Enter HS Code",
        },
      },
      {
        accessorKey: "description",
        header: "Description",
        grow: true, // takes remaining space
        enableEditing: true,
        muiEditTextFieldProps: {
          multiline: true,
          minRows: 2,
          placeholder: "Enter description",
        },
      },
    ],
    [pagination],
  );

  const renderMobileCards = () => {
    return hsCodeList.map((item, index) => (
      <Box
        key={item._id}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          backgroundColor: "#fff",
          boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
        }}
      >
        {/* Serial Number */}
        <Box sx={{ fontSize: 12, color: "#999", mb: 1 }}>
          #{index + 1 + pagination.pageIndex * pagination.pageSize}
        </Box>

        {/* HS Code */}
        <Box sx={{ mb: 1 }}>
          <b className={`text-[${globalStyle.bgColorBlue}]`}>HS Code:</b>
          <Box sx={{ fontSize: 14 }}>{item.pctCode || "-"}</Box>
        </Box>

        {/* Description */}
        <Box>
          <b className={`text-[${globalStyle.bgColorBlue}]`}>Description:</b>
          <Box sx={{ fontSize: 14, whiteSpace: "pre-wrap" }}>
            {item.description || "-"}
          </Box>
        </Box>
      </Box>
    ));
  };

  return (
    <Box className="p-4">
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap", // important for mobile
          gap: 1,
        }}
      >
        <Box>
          <Box
            component="h1"
            sx={{
              fontFamily: globalStyle.fontFamilyHeading,
              fontWeight: 700,
              color: globalStyle.bgColorBlue,
              fontSize: {
                xs: globalStyle.headingFontSizeSmall, // mobile
                sm: globalStyle.headingFontSize, // tablet+
              },
              lineHeight: 1.2,
            }}
          >
            HS Codes
          </Box>

          {/* Optional subtitle (mobile friendly) */}
          <Box
            component="p"
            sx={{
              mt: 0.5,
              fontFamily: globalStyle.fontFamilyParagraph,
              fontSize: {
                xs: 12,
                sm: 14,
              },
              color: "#666",
            }}
          >
            Manage and view all HS code records
          </Box>
        </Box>
      </Box>
      {isMobile ? (
        <MobileViewTabelList
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          pagination={pagination}
          setPagination={setPagination}
          totalResults={totalResults}
          loading={isGettingHsCodeList}
          searchLabel="HS Codes"
        >
          {renderMobileCards()}
        </MobileViewTabelList>
      ) : (
        <MaterialReactTable
          columns={columns}
          data={hsCodeList}
          manualPagination
          rowCount={totalResults}
          state={{
            // editingRow,
            showGlobalFilter: true,
            isLoading: isGettingHsCodeList,
            pagination,
          }}
          onPaginationChange={setPagination}
          onGlobalFilterChange={(value) =>
            isUndefined(value)
              ? debouncedSetFilter("")
              : debouncedSetFilter(value)
          }
          columnFilterDisplayMode={false}
          muiTablePaperProps={{
            sx: {
              backgroundColor: globalStyle.bgColorWhite,
              borderRadius: `${globalStyle.borderRadius}px`,
              fontFamily: globalStyle.fontFamily,
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              backgroundColor: globalStyle.blueColor,
              color: "#fff",
              fontSize: globalStyle.fontSize,
              fontFamily: globalStyle.fontFamilyHeading,
              fontWeight: 600,
              borderBottom: "none",
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              fontSize: globalStyle.fontSize,
              fontFamily: globalStyle.fontFamilyParagraph,
              color: "#333",
              borderBottom: `1px solid ${globalStyle.curveLightColor}`,
            },
          }}
          muiTableBodyRowProps={{
            sx: {
              backgroundColor: globalStyle.bgColorWhite,
              "&:hover": {
                backgroundColor: globalStyle.bgColor,
              },
            },
          }}
          muiTableContainerProps={{
            sx: {
              maxHeight: "none", // ✅ remove height restriction
              overflow: "visible", // ✅ allow content to expand
              borderRadius: `${globalStyle.borderRadius}px`,
            },
          }}
          muiPaginationProps={{
            sx: {
              "& .MuiTablePagination-toolbar": {
                fontFamily: globalStyle.fontFamily,
              },
              "& .Mui-selected": {
                backgroundColor: globalStyle.blueColor,
                color: "#fff",
              },
            },
          }}
          muiTableProps={{
            sx: {
              tableLayout: "fixed",
            },
          }}
        />
      )}
    </Box>
  );
};

export default HsCodeList;
